import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PackageCheck, Plus } from 'lucide-react';
import { Heading } from '../layout';
import { useUIColors } from '../../hooks/useTheme';
import PurchaseReceiptsTable from '../purchaseReceipts/PurchaseReceiptsTable';
import { PurchaseReceiptsForm } from '../forms/PurchaseReceiptsForm';
import PurchaseReceiptDetailModal from './PurchaseReceiptDetailModal';
import purchaseReceiptsAPI from '../../services/purchaseReceiptsAPI';
import purchaseOrdersAPI from '../../services/purchaseOrdersAPI';
import { getPoStatusPresentation, normalizePoStatus } from './purchaseOrderSummary';

const todayIso = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = {
  receipt_code: '',
  purchase_order_id: '',
  supplier_id: '',
  receipt_date: todayIso(),
  received_by: '',
  status: 'Borrador',
  notes: '',
  items: [],
};

const foldStatus = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const RECEIPT_TABS = [
  { key: 'all', label: 'Consolidado' },
  { key: 'pending', label: 'Por recepcionar' },
  { key: 'Borrador', label: 'Borrador' },
  { key: 'Confirmada', label: 'Confirmada' },
  { key: 'Anulada', label: 'Anulada' },
];

const BLOCKED_PO_STATUSES = new Set([
  'borrador',
  'anulada',
  'recepcion conforme',
  'recepcion no conforme',
  'pagado atrasado',
  'pagado a tiempo',
]);

const INVENTORY_ITEM_TYPES = new Set(['materias primas', 'insumos', 'upselling']);

const foldType = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const isInventoryItem = (item) => INVENTORY_ITEM_TYPES.has(foldType(item?.item_type));

const isReceivablePo = (order) =>
  !BLOCKED_PO_STATUSES.has(foldStatus(order?.status)) && Number(order?.inventory_items_count || 0) > 0;

const formatMoney = (value, currency = 'CLP') =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: ['CLP', 'USD', 'EUR'].includes(String(currency || 'CLP').toUpperCase())
      ? String(currency || 'CLP').toUpperCase()
      : 'CLP',
    maximumFractionDigits: String(currency || 'CLP').toUpperCase() === 'CLP' ? 0 : 2,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-CL');
};

const mapPoItems = (po) =>
  (Array.isArray(po?.items) ? po.items : []).filter(isInventoryItem).map((item) => ({
    id: null,
    purchase_order_item_id: item.id,
    item_type: item.item_type || '',
    item_code: item.item_code || '',
    description: item.description || '',
    quantity_ordered: item.quantity ?? 0,
    quantity_received: item.quantity ?? 0,
    unit: item.unit || 'UN',
    notes: '',
  }));

const PaintPurchaseReceiptsWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();

  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [listTab, setListTab] = useState('pending');

  const [orders, setOrders] = useState([]);
  const [orderCache, setOrderCache] = useState({});

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [detailReceipt, setDetailReceipt] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadReceipts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await purchaseReceiptsAPI.getAll();
      setReceipts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'No se pudieron cargar las recepciones.');
      setReceipts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      const data = await purchaseOrdersAPI.getAll();
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading PO list for receipts:', err);
    }
  }, []);

  useEffect(() => {
    loadReceipts();
    loadOrders();
  }, [loadReceipts, loadOrders]);

  const draftByPoId = useMemo(() => {
    const map = new Map();
    receipts.forEach((row) => {
      if (foldStatus(row.status) === 'borrador' && row.purchase_order_id) {
        map.set(Number(row.purchase_order_id), row);
      }
    });
    return map;
  }, [receipts]);

  const pendingOrders = useMemo(() => orders.filter(isReceivablePo), [orders]);

  const purchaseOrderOptions = useMemo(
    () =>
      orders.filter(isReceivablePo).map((o) => ({
        value: String(o.id),
        label: `${o.po_code || ''} · ${o.supplier_name || 'Sin proveedor'}`.trim(),
      })),
    [orders]
  );

  const visibleReceipts = useMemo(() => {
    if (listTab === 'all' || listTab === 'pending') return receipts;
    return receipts.filter((row) => foldStatus(row.status) === foldStatus(listTab));
  }, [receipts, listTab]);

  const listTabs = useMemo(
    () =>
      RECEIPT_TABS.map((tab) => {
        let count = receipts.length;
        if (tab.key === 'pending') count = pendingOrders.length;
        else if (tab.key !== 'all') {
          count = receipts.filter((row) => foldStatus(row.status) === foldStatus(tab.key)).length;
        }
        return { ...tab, count };
      }),
    [receipts, pendingOrders]
  );

  const listSubtitle = loading
    ? 'Cargando…'
    : error ||
      (listTab === 'pending'
        ? `${pendingOrders.length} OC por recepcionar`
        : `${visibleReceipts.length} recepciones`);

  const loadPoDetail = async (poId) => {
    const key = String(poId);
    if (orderCache[key]?.items) return orderCache[key];
    const full = await purchaseOrdersAPI.getById(poId);
    setOrderCache((prev) => ({ ...prev, [key]: full }));
    return full;
  };

  const openCreate = async (preselectedPo = null) => {
    if (preselectedPo?.id) {
      const existingDraft = draftByPoId.get(Number(preselectedPo.id));
      if (existingDraft?.id) {
        await openEdit(existingDraft);
        return;
      }
    }
    setIsEditing(false);
    setEditingId(null);
    setFormError('');
    const base = { ...EMPTY_FORM, receipt_date: todayIso(), items: [] };
    if (preselectedPo?.id) {
      try {
        const po = await loadPoDetail(preselectedPo.id);
        setFormData({
          ...base,
          purchase_order_id: String(po.id),
          supplier_id: po.supplier_id ? String(po.supplier_id) : '',
          items: mapPoItems(po),
        });
      } catch (err) {
        setFormData(base);
        setFormError(err?.message || 'No se pudieron cargar los ítems de la OC.');
      }
    } else {
      setFormData(base);
    }
    setShowForm(true);
  };

  const openDetail = async (row) => {
    setDetailLoading(true);
    try {
      const full = await purchaseReceiptsAPI.getById(row.id);
      setDetailReceipt(full);
    } catch (err) {
      window.alert(err?.message || 'No se pudo cargar la recepción.');
    } finally {
      setDetailLoading(false);
    }
  };

  const openEdit = async (receipt) => {
    const source = receipt?.items ? receipt : await purchaseReceiptsAPI.getById(receipt.id);
    setDetailReceipt(null);
    setIsEditing(true);
    setEditingId(source.id);
    setFormError('');
    setFormData({
      receipt_code: source.receipt_code || '',
      purchase_order_id: source.purchase_order_id ? String(source.purchase_order_id) : '',
      supplier_id: source.supplier_id ? String(source.supplier_id) : '',
      receipt_date: source.receipt_date ? String(source.receipt_date).slice(0, 10) : todayIso(),
      received_by: source.received_by || '',
      status: source.status || 'Borrador',
      notes: source.notes || '',
      items:
        Array.isArray(source.items) && source.items.length
          ? source.items.filter(isInventoryItem).map((item) => ({
              id: item.id,
              purchase_order_item_id: item.purchase_order_item_id,
              item_type: item.item_type || '',
              item_code: item.item_code || '',
              description: item.description || '',
              quantity_ordered: item.quantity_ordered ?? 0,
              quantity_received: item.quantity_received ?? 0,
              unit: item.unit || 'UN',
              notes: item.notes || '',
            }))
          : [],
    });
    setShowForm(true);
  };

  const handleChange = async (key, value) => {
    if (key === 'purchase_order_id') {
      setFormData((prev) => ({ ...prev, purchase_order_id: value, items: [] }));
      if (!value) return;
      try {
        const po = await loadPoDetail(value);
        setFormData((prev) => ({
          ...prev,
          purchase_order_id: value,
          supplier_id: po.supplier_id ? String(po.supplier_id) : '',
          items: mapPoItems(po),
        }));
      } catch (err) {
        setFormError(err?.message || 'No se pudieron cargar los ítems de la OC.');
      }
      return;
    }
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const buildPayload = (statusOverride) => {
    const poId = Number(formData.purchase_order_id || 0);
    const items = (formData.items || [])
      .filter(isInventoryItem)
      .map((item) => ({
        id: item.id || null,
        purchase_order_item_id: Number(item.purchase_order_item_id || 0) || null,
        item_type: String(item.item_type || '').trim(),
        item_code: String(item.item_code || '').trim(),
        description: String(item.description || '').trim(),
        quantity_ordered: Number(item.quantity_ordered || 0),
        quantity_received: Number(item.quantity_received || 0),
        unit: String(item.unit || 'UN').trim() || 'UN',
        notes: String(item.notes || '').trim(),
      }))
      .filter((item) => item.quantity_received > 0 && (item.description || item.item_code));
    return {
      poId,
      items,
      payload: {
        purchase_order_id: poId,
        supplier_id: Number(formData.supplier_id || 0) || null,
        receipt_date: formData.receipt_date || todayIso(),
        received_by: String(formData.received_by || '').trim(),
        status: String(statusOverride || formData.status || 'Borrador').trim() || 'Borrador',
        notes: String(formData.notes || '').trim(),
        items,
      },
    };
  };

  const persistReceipt = async (statusOverride) => {
    const { poId, items, payload } = buildPayload(statusOverride);
    if (!poId) {
      setFormError('Debes seleccionar una orden de compra.');
      return null;
    }
    if (!items.length) {
      setFormError('Indica cantidad recibida en al menos un ítem de inventario.');
      return null;
    }
    setIsSaving(true);
    setFormError('');
    try {
      const saved =
        isEditing && editingId
          ? await purchaseReceiptsAPI.update(editingId, payload)
          : await purchaseReceiptsAPI.create(payload);
      setShowForm(false);
      setEditingId(null);
      setFormData({ ...EMPTY_FORM });
      await Promise.all([loadReceipts(), loadOrders()]);
      return saved;
    } catch (err) {
      setFormError(err?.message || 'No se pudo guardar la recepción.');
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = (statusOverride) => persistReceipt(statusOverride || 'Borrador');

  const handleConfirmFromForm = async () => {
    const saved = await persistReceipt('Confirmada');
    if (saved?.id && foldStatus(saved.status) !== 'confirmada') {
      try {
        await purchaseReceiptsAPI.confirm(saved.id);
        await Promise.all([loadReceipts(), loadOrders()]);
      } catch (err) {
        window.alert(err?.message || 'Se guardó, pero no se pudo confirmar la recepción.');
      }
    }
  };

  const handleConfirmRow = async (row) => {
    if (!row?.id) return;
    try {
      await purchaseReceiptsAPI.confirm(row.id);
      setDetailReceipt(null);
      await Promise.all([loadReceipts(), loadOrders()]);
    } catch (err) {
      window.alert(err?.message || 'No se pudo confirmar la recepción.');
    }
  };

  const handleBulkDelete = async (ids) => {
    if (!ids?.length) return false;
    const ok = window.confirm(
      ids.length === 1 ? '¿Eliminar esta recepción?' : `¿Eliminar ${ids.length} recepciones?`
    );
    if (!ok) return false;
    await purchaseReceiptsAPI.bulkDelete(ids);
    await Promise.all([loadReceipts(), loadOrders()]);
    return true;
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: 6 }}>
          Recepción
        </Heading>
        <p style={{ margin: 0, maxWidth: 720, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
          Recibe mercadería contra una OC. Solo entran ítems con inventario (materias primas, insumos y upselling).
          Servicios y transporte no se recepcionan. Al confirmar, la OC pasa a Recepción Conforme y el stock se actualiza.
        </p>
      </div>

      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: ui.page,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Recepciones</h3>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>{listSubtitle}</p>
          </div>
          <button
            type="button"
            onClick={() => openCreate()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 36,
              padding: '0 14px',
              borderRadius: 8,
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> Nueva recepción
          </button>
        </div>

        <div
          role="tablist"
          aria-label="Vistas de recepción"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '0 12px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            overflowX: 'auto',
          }}
        >
          {listTabs.map((item) => {
            const active = listTab === item.key;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setListTab(item.key)}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '10px 12px',
                  marginBottom: -1,
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? ui.text : ui.muted,
                  borderBottom: active ? '2px solid #16a34a' : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>{item.label}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: active ? (isDarkMode ? '#86efac' : '#15803d') : ui.muted,
                    background: active
                      ? isDarkMode
                        ? 'rgba(22, 163, 74, 0.2)'
                        : 'rgba(22, 163, 74, 0.12)'
                      : isDarkMode
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.05)',
                    borderRadius: 999,
                    padding: '1px 7px',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {item.count}
                </span>
              </button>
            );
          })}
        </div>

        {listTab === 'pending' ? (
          <PendingOrdersTable
            rows={pendingOrders}
            loading={loading}
            isDarkMode={isDarkMode}
            onReceive={(order) => openCreate(order)}
          />
        ) : (
          <PurchaseReceiptsTable
            rows={visibleReceipts}
            loading={loading || detailLoading}
            error={error}
            isDarkMode={isDarkMode}
            hideChrome
            onRowClick={openDetail}
            onBulkDelete={handleBulkDelete}
            onConfirm={handleConfirmRow}
          />
        )}
      </div>

      <PurchaseReceiptsForm
        isOpen={showForm}
        onClose={() => !isSaving && setShowForm(false)}
        onSave={handleSave}
        onConfirm={handleConfirmFromForm}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        isEditing={isEditing}
        formData={formData}
        onChange={handleChange}
        formError={formError}
        purchaseOrderOptions={purchaseOrderOptions}
      />

      <PurchaseReceiptDetailModal
        isOpen={Boolean(detailReceipt)}
        onClose={() => setDetailReceipt(null)}
        purchaseReceipt={detailReceipt}
        isDarkMode={isDarkMode}
        onEdit={openEdit}
        onConfirm={handleConfirmRow}
      />
    </div>
  );
};

const PendingOrdersTable = ({ rows = [], loading = false, isDarkMode = false, onReceive }) => {
  const ui = useUIColors();
  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center', color: ui.muted }}>Cargando órdenes…</div>;
  }
  if (!rows.length) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: ui.muted }}>
        No hay OC pendientes de recepción.
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
        <thead>
          <tr style={{ background: ui.header, borderBottom: `1px solid ${ui.border}` }}>
            {['OC', 'Proveedor', 'Fecha', 'Estado', 'Ítems', 'Total', 'Acciones'].map((h) => (
              <th
                key={h}
                style={{
                  padding: '8px 10px',
                  textAlign: h === 'Ítems' || h === 'Total' || h === 'Acciones' ? 'right' : 'left',
                  fontSize: 11,
                  fontWeight: 700,
                  color: ui.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const st = getPoStatusPresentation(normalizePoStatus(row.status), isDarkMode);
            return (
              <tr key={row.id} style={{ borderBottom: `1px solid ${ui.border}` }}>
                <td style={{ padding: '9px 10px', fontSize: 12, fontWeight: 700, color: ui.text }}>
                  {row.po_code || '—'}
                </td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: ui.text }}>{row.supplier_name || '—'}</td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: ui.muted }}>{formatDate(row.order_date)}</td>
                <td style={{ padding: '9px 10px' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 600,
                      background: st.bg,
                      color: st.color,
                      border: `1px solid ${st.border}`,
                    }}
                  >
                    {st.label}
                  </span>
                </td>
                <td style={{ padding: '9px 10px', fontSize: 12, textAlign: 'right', color: ui.text }}>
                  {row.inventory_items_count ?? row.items_count ?? 0}
                </td>
                <td style={{ padding: '9px 10px', fontSize: 12, textAlign: 'right', fontWeight: 600, color: ui.text }}>
                  {formatMoney(row.total_amount, row.currency)}
                </td>
                <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                  <button
                    type="button"
                    onClick={() => onReceive?.(row)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      height: 30,
                      padding: '0 10px',
                      borderRadius: 6,
                      border: 'none',
                      background: '#0f766e',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    <PackageCheck size={14} /> Recepcionar
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PaintPurchaseReceiptsWorkspace;
